import { Close, Publish, Redo, Save, Undo } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Divider } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { object, string } from 'yup';
import { useUpdateDesign } from '~/api/designs';
import { IconButton, IconButtonTooltip } from '~/components/IconButton';
import { Tooltip } from '~/components/Tooltip';
import { SelectFolderDialog } from '~/components/dialogs/SelectContentFolder';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { InlineEditTitle, Toolbar } from '~/components/toolbar';
import { useConfirmDialog } from '~/hooks/dialogs';
import { i18n } from '~/lib/i18n';
import { useStudio } from '../../context';
import type { DesignEdit__Design as Design } from '../DesignEdit.generated';
import { usePublish, useSave } from '../lib';
import { StudioFileMenu } from './StudioFileMenu';

export interface StudioToolbarProps {
  design: Design;
}

export const StudioToolbar = ({ design }: StudioToolbarProps) => {
  const { t } = useTranslation();
  const { canRedo, canUndo, dirty, redo, undo } = useStudio();
  const navigate = useNavigate();
  const [updateDesign] = useUpdateDesign();
  const [confirmClose, confirmCloseProps] = useConfirmDialog();

  const [selectFolderOpen, setSelectFolderOpen] = useState(false);

  const [publish, publishLoading] = usePublish(design);
  const [handleSave, saveLoading] = useSave(design);

  const handlePublish = useCallback(
    async (folderId: number) => {
      await publish(folderId);
      setSelectFolderOpen(false);
    },
    [publish],
  );

  const handlePublishClick = useCallback(async () => {
    if (design.publishedApp?.contentFolderId) {
      await handlePublish(design.publishedApp.contentFolderId);
    } else {
      setSelectFolderOpen(true);
    }
  }, [design.publishedApp, handlePublish]);

  return (
    <>
      <Toolbar
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box>
              <IconButton
                label={t('pages.studio.edit.toolbar.undo')}
                disabled={!canUndo}
                onClick={undo}
                color="secondary"
              >
                <Undo />
              </IconButton>
              <IconButton
                label={t('pages.studio.edit.toolbar.redo')}
                disabled={!canRedo}
                onClick={redo}
                color="secondary"
              >
                <Redo />
              </IconButton>
            </Box>
            <Divider orientation="vertical" />
            <LoadingButton
              color="primary"
              disabled={!dirty}
              loading={saveLoading}
              loadingPosition="start"
              onClick={handleSave}
              startIcon={<Save />}
              variant="contained"
            >
              {t('common.actions.save')}
            </LoadingButton>
            <Tooltip title={t('pages.studio.edit.toolbar.publish_tooltip')}>
              <LoadingButton
                color="primary"
                disabled={saveLoading || selectFolderOpen}
                loading={publishLoading}
                loadingPosition="start"
                onClick={handlePublishClick}
                startIcon={<Publish />}
                variant="contained"
              >
                {t('pages.studio.edit.toolbar.publish')}
              </LoadingButton>
            </Tooltip>
            <Divider orientation="vertical" />
            <IconButtonTooltip label={t('common.actions.close')}>
              <Button
                aria-label={t('common.actions.close')}
                onClick={async () => {
                  if (dirty && !(await confirmClose())) return;
                  history.length > 1 ? navigate(-1) : navigate('..');
                }}
                variant="outlined"
                sx={{ minWidth: 0, padding: 1 }}
              >
                <Close />
              </Button>
            </IconButtonTooltip>
          </Box>
        }
        titleEditor={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StudioFileMenu
              design={design}
              onSave={handleSave}
              saveLoading={saveLoading}
              onPublish={handlePublishClick}
              publishLoading={publishLoading}
            />
            <Divider orientation="vertical" flexItem />
            <InlineEditTitle
              inputId="studio-item-name"
              value={design.name}
              update={(name) => updateDesign({ variables: { id: design.id, patch: { name } } })}
              tooltip={t('pages.studio.edit.toolbar.edit_name_tooltip')}
              validationSchema={object({
                name: string()
                  .required(i18n.t('pages.studio.edit.toolbar.validation.name_required'))
                  .trim(),
              })}
            />
          </Box>
        }
      />
      <ConfirmDialog
        {...confirmCloseProps}
        confirm={t('common.dialogs.confirm')}
        prompt={
          <span>
            {t('pages.studio.edit.toolbar.confirm_close.prompt')}{' '}
            <b>{t('common.warnings.cannot_be_undone')}</b>
          </span>
        }
        title={t('pages.studio.edit.toolbar.confirm_close.title')}
      />
      <SelectFolderDialog
        description={t('pages.studio.edit.toolbar.select_folder_description')}
        open={selectFolderOpen}
        onClose={() => setSelectFolderOpen(false)}
        onSubmit={(folderId) => handlePublish(+folderId)}
      />
    </>
  );
};
